import { ChevronDown, ChevronRight } from 'lucide-react';
import { type Dispatch, type SetStateAction, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MenuItem } from './MenuItem';
import styles from './Sidebar.module.css';
import type { SidebarMenuItem } from './sidebar-types';
import { useSidebarShowSubItems } from './useSidebarShowSubItems';
import { cn } from '@/lib/cn';

const SIDEBAR_SCROLL_STORAGE_KEY = 'loans-sidebar-scroll-top';

interface MenuBlockProps {
  menuItems: SidebarMenuItem[];
  isOpened: boolean;
  setIsOpened?: Dispatch<SetStateAction<boolean>>;
  paddingY?: string;
  isSearchUsed?: boolean;
  search?: string;
}

export const MenuBlock = ({
  search,
  isOpened,
  menuItems,
  setIsOpened,
  isSearchUsed,
  paddingY = '8px',
}: MenuBlockProps) => {
  const { openItems, toggleOpenItem } = useSidebarShowSubItems();
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const savedTop = window.sessionStorage.getItem(SIDEBAR_SCROLL_STORAGE_KEY);
    if (!savedTop) return;

    node.scrollTop = Number(savedTop) || 0;
  }, [location.pathname]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const persistScroll = () => {
      window.sessionStorage.setItem(SIDEBAR_SCROLL_STORAGE_KEY, String(node.scrollTop));
    };

    persistScroll();
    node.addEventListener('scroll', persistScroll, { passive: true });
    return () => node.removeEventListener('scroll', persistScroll);
  }, []);

  return (
    <div
      ref={scrollRef}
      className={cn(
        'relative flex w-full flex-col items-center justify-start gap-2 overflow-y-auto py-4 select-none',
        styles.scroll,
      )}
    >
      {menuItems.map((item, i) => {
        const { label, subItems, subSections } = item;
        const id = `item_${i}`;
        const isOpenItem = openItems[id] ?? item.defaultOpen ?? false;
        const subSectionsHeight = (subSections ?? []).reduce((sum, _section, sectionIndex) => {
          const sectionId = `${id}_section_${sectionIndex}`;
          const isOpenSection = openItems[sectionId] ?? false;
          return sum + 34 + (isOpenSection ? _section.items.length * 48 + 4 : 0);
        }, 0);

        return (
          <div className="flex w-full flex-col" key={`${label}_${id}`}>
            <MenuItem
              item={item}
              search={search}
              isOpenItem={isOpenItem}
              toggleOpenItem={() => toggleOpenItem(id)}
              isOpened={isOpened}
              paddingY={paddingY}
              setIsOpened={setIsOpened}
              isSearchUsed={isSearchUsed}
            />
            {(subItems || subSections) && (
              <div
                className={cn(
                  'ml-5 flex h-0 w-auto flex-col items-center justify-start gap-2 overflow-y-hidden border-l border-l-gray-700 transition-all duration-500 ease-in-out select-none',
                  styles.scroll,
                  {
                    'pt-2': isOpenItem,
                  },
                )}
                style={{
                  height: isOpenItem
                    ? `${(subItems?.length ?? 0) * 48 + subSectionsHeight}px`
                    : undefined,
                }}
              >
                {subItems?.map((subItem, j) => {
                  const idSub = `${id}_${j}`;
                  const isOpenSubItem = openItems[idSub];
                  return (
                    <MenuItem
                      item={subItem}
                      key={idSub}
                      search={search}
                      isOpenItem={isOpenSubItem}
                      toggleOpenItem={() => toggleOpenItem(idSub)}
                      isOpened={isOpened}
                      paddingY={paddingY}
                      setIsOpened={setIsOpened}
                      isSearchUsed={isSearchUsed}
                    />
                  );
                })}
                {subSections?.map((section, sectionIndex) => (
                  <div className="w-full" key={`${id}_section_${section.label}`}>
                    {(() => {
                      const sectionId = `${id}_section_${sectionIndex}`;
                      const isOpenSection = openItems[sectionId] ?? false;

                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => toggleOpenItem(sectionId)}
                            className="flex w-full items-center gap-2 px-4 pb-1 pt-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 transition-colors hover:text-gray-300"
                          >
                            {isOpenSection ? (
                              <ChevronDown className="h-3 w-3 shrink-0" />
                            ) : (
                              <ChevronRight className="h-3 w-3 shrink-0" />
                            )}
                            <span>{section.label}</span>
                          </button>
                          <div
                            className={cn(
                              'h-0 overflow-y-hidden transition-all duration-500 ease-in-out',
                              {
                                'pt-1': isOpenSection,
                              },
                            )}
                            style={{
                              height: isOpenSection ? `${section.items.length * 48}px` : undefined,
                            }}
                          >
                            {section.items.map((subItem, itemIndex) => {
                              const idSub = `${sectionId}_${itemIndex}`;
                              const isOpenSubItem = openItems[idSub];
                              return (
                                <MenuItem
                                  item={subItem}
                                  key={idSub}
                                  search={search}
                                  isOpenItem={isOpenSubItem}
                                  toggleOpenItem={() => toggleOpenItem(idSub)}
                                  isOpened={isOpened}
                                  paddingY={paddingY}
                                  setIsOpened={setIsOpened}
                                  isSearchUsed={isSearchUsed}
                                />
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
